/**
 * Structured data (JSON-LD) and document head for the public events list route.
 * Breadcrumb labels in JSON-LD use English (common practice); UI meta strings use i18n.
 */

import { env } from '@web/src/lib/env.client'
import i18n from '@web/src/lib/intl/i18n'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import { absoluteUrlForPath, baseUrl } from '@web/src/lib/utilsClient'

const LIST_PATH = '/event/list'

export type EventListSeoItem = {
  name: string
  path: string
  imageUrl?: string | null
}

export type EventListSeoLoaderData = {
  seoList: {
    items: EventListSeoItem[]
    totalCount: number
  }
}

type EventListSearchForSeo = {
  name?: string
  tags?: string
  categories?: string
  excludeTags?: string
  location?: string
  sortBy?: string
  pastEvents?: boolean
  myEvents?: boolean
  pendingEvents?: boolean
  showHosting?: boolean
}

/** True when URL search filters narrow the catalog (not my-events / invitations modes). */
function hasActiveEventListFilters(search: EventListSearchForSeo): boolean {
  if (search.pastEvents) return true
  if (search.name?.trim()) return true
  if (search.tags?.trim()) return true
  if (search.categories?.trim()) return true
  if (search.excludeTags?.trim()) return true
  if (search.location?.trim()) return true
  if (search.sortBy && search.sortBy !== 'START_DATE') return true
  return false
}

function eventListPageTitle(search: EventListSearchForSeo): string {
  const tr = i18n.t.bind(i18n)
  if (search.myEvents === true) return tr('My Events')
  if (search.pendingEvents === true) return tr('Invitations')
  return tr('Discover Events')
}

function eventListPageDescription(search: EventListSearchForSeo): string {
  const tr = i18n.t.bind(i18n)
  if (search.myEvents === true) {
    return tr(
      'View and manage swinger and lifestyle events you host or attend on our platform. See upcoming dates, RSVPs, and details for gatherings you created or joined.'
    )
  }
  if (search.pendingEvents === true) {
    return tr(
      'Review invitations to swinger and lifestyle events on our platform. See pending invites, dates, and details, and respond when you are ready from one list.'
    )
  }
  return tr(
    'Browse swinger and lifestyle events on our platform. Filter by date, location, and tags to find parties, meetups, and social gatherings that fit your interests.'
  )
}

/** English labels for BreadcrumbList (aligned with place detail SEO). */
function eventListBreadcrumbThirdLabelEnglish(search: EventListSearchForSeo): string {
  if (search.myEvents === true) return 'My Events'
  if (search.pendingEvents === true) return 'Invitations'
  return 'Discover Events'
}

function buildEventListBreadcrumbJsonLd(search: EventListSearchForSeo): Record<string, unknown> {
  const thirdLabel = eventListBreadcrumbThirdLabelEnglish(search)
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: absoluteUrlForPath('/') },
      { '@type': 'ListItem', position: 2, name: 'Event Categories', item: absoluteUrlForPath('/event') },
      { '@type': 'ListItem', position: 3, name: thirdLabel, item: absoluteUrlForPath(LIST_PATH) },
    ],
  }
}

function buildEventListItemListJsonLd(input: {
  hasFilters: boolean
  items: EventListSeoItem[]
  totalCount: number
}): Record<string, unknown> {
  const tr = i18n.t.bind(i18n)
  const base: Record<string, unknown> = {
    '@type': 'ItemList',
    numberOfItems: input.totalCount,
  }

  if (input.hasFilters) {
    base.name = tr('Filtered event search results')
  }

  if (input.items.length > 0) {
    base.itemListElement = input.items.map((row, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: row.name,
      item: absoluteUrlForPath(row.path),
      ...(row.imageUrl ? { image: row.imageUrl } : {}),
    }))
  }

  return base
}

export function eventListPageHeadFromLoaderData(
  loaderData: EventListSeoLoaderData | null | undefined,
  search: EventListSearchForSeo
) {
  const pageTitle = eventListPageTitle(search)
  const description = eventListPageDescription(search)
  const canonicalUrl = `${baseUrl()}${LIST_PATH}`
  const hasFilters = hasActiveEventListFilters(search)

  const items = loaderData?.seoList.items ?? []
  const totalCount = loaderData?.seoList.totalCount ?? 0

  const searchResultsPage: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'SearchResultsPage',
    url: canonicalUrl,
    name: pageTitle,
    description,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${baseUrl()}${LIST_PATH}?name={query}`,
      },
      queryInput: 'required name=query',
    },
    mainEntity: buildEventListItemListJsonLd({ hasFilters, items, totalCount }),
  }

  const jsonLd: Record<string, unknown>[] = [searchResultsPage, buildEventListBreadcrumbJsonLd(search)]

  const ogImage = items[0]?.imageUrl ?? '/logo-og-image.webp'

  return seo({
    title: `${pageTitle} | ${env.VITE_APP_NAME}`,
    description,
    url: canonicalUrl,
    image: ogImage,
    robots: robotsMetaForEnvironment({
      production: 'index, follow',
      staging: 'noindex,nofollow',
    }),
    jsonLd,
  })
}
